import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {Alert, Card} from 'antd';
import {makeSelectTabOperations} from 'redux/store/auth';
import {unwrap} from 'pages/kybDashboard/kybUtils';
import {KYC_HUB_DOCUMENT_TYPES} from './resolvers';
import {SUBJECT_PERMISSION_TAB} from './kycHubUtils';
import {HubDocuments} from './HubDocuments';
import {HubNotes} from './HubNotes';
import {HubActivity} from './HubActivity';
import styles from './kycHub.module.scss';

const TABS = [
  {key: 'documents', label: 'Documents'},
  {key: 'notes', label: 'Internal notes'},
  {key: 'activity', label: 'Activity'},
];

/**
 * Supplemental documents, internal notes and the activity timeline for one
 * subject.
 *
 * `subjectId` is the user ID for `INDIVIDUAL` and the institutional account ID
 * for `INSTITUTIONAL` — never an identity-card, Smile job or contact user ID.
 * Mount it with `key={subjectId}` so switching subjects starts clean.
 *
 * `kyb` carries the KYB account's own rules into the documents tab (status
 * gates, the checklist join and the declared beneficial owners a file can be
 * attached to); leave it out for an individual.
 *
 * `summary` is an optional list of `{label, value}` shown above the tabs:
 * who the subject is, as the page mounting the hub already knows it.
 */
export const KycHubPanel = ({subjectType, subjectId, kyb, summary}) => {
  const [activeTab, setActiveTab] = useState('documents');
  const operations = useSelector(state =>
    makeSelectTabOperations(state, SUBJECT_PERMISSION_TAB[subjectType]),
  );
  const canView = Boolean(operations?.view);
  const canUpdate = Boolean(operations?.update);

  const {data: typesData} = useQuery(KYC_HUB_DOCUMENT_TYPES, {
    variables: {input: {subjectType}},
    skip: !canView,
  });
  const documentTypes = unwrap(typesData?.kycHubDocumentTypes).data || [];

  const subject = {subjectType, subjectId};

  return (
    <Card title="KYC Hub" className={styles.card}>
      {!canView ? (
        <Alert
          type="info"
          showIcon
          message={`You need view access to ${
            subjectType === 'INSTITUTIONAL'
              ? 'Business Verification'
              : 'the KYC Dashboard'
          } to see supplemental documents, notes and activity.`}
        />
      ) : (
        <>
          {summary?.length > 0 && (
            <dl className={styles.subjectSummary}>
              {summary.map(item => (
                <div key={item.label} className={styles.subjectItem}>
                  <dt>{item.label}</dt>
                  <dd>{item.value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className={styles.tabBar} role="tablist">
            {TABS.map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={
                  activeTab === tab.key ? styles.tabActive : styles.tabButton
                }
                onClick={() => setActiveTab(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'documents' && (
            <HubDocuments
              subject={subject}
              documentTypes={documentTypes}
              canUpdate={canUpdate}
              kyb={kyb}
            />
          )}
          {activeTab === 'notes' && (
            <HubNotes subject={subject} canUpdate={canUpdate} />
          )}
          {activeTab === 'activity' && <HubActivity subject={subject} />}
        </>
      )}
    </Card>
  );
};
